
import { emptyGrid, IGridResult } from '@api/models'
import { DPDataTable } from 'infrastructure/framework/DPForms/DPDataTable'
import { DPFormField } from 'infrastructure/framework/DPForms/DPFormField'
import { DPInputDropdown } from 'infrastructure/framework/DPForms/DPInputDropdown'
import { DPTableSearchForm } from 'infrastructure/framework/DPForms/DPTableSearchForm'
import { IDataTableColumn } from 'infrastructure/framework/DPForms/DPDataTable'
import { PageHeader } from 'infrastructure/framework/PageHeader'
import { useApiCall } from '@hooks/useApiCall'
import { useApiClient } from '@hooks/useApiClient'
import { useTranslation } from '@hooks/useTranslation'
import { KycApplicationApiClient, IKycApplicationOverviewModel } from 'api'
import { DealerApiClient } from 'api'
import { IDropdownOption } from 'model'
import React, { useEffect, useMemo, useState } from 'react'
import { dateToDanishDate } from 'shared/dateTools'
import { routes } from 'routing'
import { useNavigate } from 'react-router'
import { useSession } from '@hooks/useSession'


interface ISearchParameters {
    dealerId: number | null
}

export function CasesPage() {
    const session = useSession()
    const initialValues: ISearchParameters = useMemo(() => ({
        dealerId: session.currentDealer?.id ?? null
    }), [session.currentDealer?.id])
    const $t = useTranslation('Administration.Cases')
    const navigate = useNavigate()
    const kycApiClient = useApiClient(p => new KycApplicationApiClient(p))
    const dealerApiClient = useApiClient(p => new DealerApiClient(p))
    const [applications, setApplications] = useState<IGridResult<IKycApplicationOverviewModel>>(emptyGrid)
    const [dealers, , getDealers] = useApiCall<Array<IDropdownOption<number>>>(dealerApiClient.getDealersDropdown, [])
    const [tableState, setTableState] = useState({ pageNumber: 1, pageSize: 10 })
    const [searchParams, setSearchParams] = useState<ISearchParameters>({ ...initialValues })
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        getApplicationList()
    }, [tableState, searchParams])

    useEffect(() => {
        getDealers()
    }, [])

    const getApplicationList = () => {
        setIsLoading(true)
        if (!searchParams.dealerId) {
            setApplications(emptyGrid)
            setIsLoading(false)
            return
        }
        kycApiClient
            .getDealerFailedApplications(searchParams.dealerId, tableState)
            .then((data: any) => {
                if (!data) {
                    setApplications(emptyGrid)
                    return
                }
                setApplications(data.data)
            })
            .finally(() => setIsLoading(false))
    }

    function handleSubmit(values: ISearchParameters) {
        setSearchParams(values)
    }

    function handleRowSelect({ referenceId }: IKycApplicationOverviewModel) {
        const needsDealerSwitch =
            session.dealers.some(d => d.id === searchParams.dealerId) &&
            searchParams.dealerId !== session.currentDealer?.id

        if (needsDealerSwitch) {
            session.changeDealer(searchParams.dealerId!)
        }

        setTimeout(() => navigate(routes.applicationKycApplication(referenceId)), needsDealerSwitch ? 100 : 0)
    }

    return (
        <article>
            <PageHeader text={$t('CasesHeader')} />

            <DPTableSearchForm
                initialValues={initialValues}
                onSubmit={handleSubmit}
                column1={
                    <DPFormField name="dealerId" label={$t('Dealer')} inline={false}>
                        <DPInputDropdown name="dealerId" options={dealers} clearable search disabled={isLoading}/>
                    </DPFormField>
                }
            />

            <div className='mt-4'>
                <DPDataTable
                    columns={columns($t)}
                    onRowSelect={handleRowSelect}
                    data={applications}
                    initialPagination={{ pageNumber: 1, pageSize: 10 }}
                    isLoading={isLoading}
                    onStateChange={setTableState}
                />
            </div>
        </article>
    )
}

const columns = (
    $t: any
): Array<IDataTableColumn<IKycApplicationOverviewModel>> => [
    {
        key: 'caseId',
        name: $t('Id'),
    },
    {
        key: 'dateReceived',
        name: $t('DateReceived'),
        onRender: ({ dateReceived }) => dateToDanishDate(dateReceived),
    },
    {
        key: 'agreementName',
        name: $t('AgreementName'),
    },
    {
        key: 'status',
        name: $t('Status'),
        onRender: ({ statusPlain, statusId }) => `${statusId} - ${statusPlain}`,
    },
    {
        key: 'salesPerson',
        name: $t('SalesPerson'),
    },
    {
        key: 'submitErrorMessage',
        name: $t('Error'),
        maxWidth: 30,
        onRender: ({ submitErrorMessage }) => (
            <span title={submitErrorMessage}>{submitErrorMessage}</span>
        ),
    }
]
